import {
  BaseLayoutManager,
  detectMode,
  detectOrientation,
} from '@bn-games/layout-core';
import {
  COACH_PANEL_WIDTH_PX,
  POND_CENTRE_CLEARANCE_PX,
  POND_GRID,
  POND_SEAT_DEPTH_RATIO,
  POND_SIDE_HEIGHT_RATIO,
  POND_SIDE_INSET_RATIO,
  POND_SIDE_WIDTH_RATIO,
  SEAT_ZONE_MAX_RATIO,
  SEAT_ZONE_THICKNESS_PX,
  TABLE_MARGIN_DESKTOP_PX,
  TABLE_MARGIN_MOBILE_PX,
} from '../utils/constants.js';
import type { LayoutConfig, Rect, SeatPosition, SeatZone } from './types.js';

const EMPTY_RECT: Rect = { x: 0, y: 0, width: 0, height: 0 };

/**
 * Four seat bands hugging the inner edges of the table. Bottom + top span the
 * full width; left + right fill the height between them so the four bands
 * never overlap. Tile rotations point each seat's tiles at the centre. Works
 * for any rectangle, not just a square — the table fills the full viewport
 * (like domino's canvas), so on a wide desktop the bottom/top bands run
 * wider than the left/right bands run tall.
 */
function computeSeatZones(table: Rect, thick: number): Record<SeatPosition, SeatZone> {
  const { x, y, width, height } = table;
  return {
    bottom: { position: 'bottom', rotation: 0, x, y: y + height - thick, width, height: thick },
    top: { position: 'top', rotation: 180, x, y, width, height: thick },
    left: {
      position: 'left',
      rotation: 90,
      x,
      y: y + thick,
      width: thick,
      height: height - thick * 2,
    },
    right: {
      position: 'right',
      rotation: 270,
      x: x + width - thick,
      y: y + thick,
      width: thick,
      height: height - thick * 2,
    },
  };
}

/**
 * The four discard blocks, arranged around the pond's centre.
 *
 * **The four blocks never overlap because bottom/top own the grid's middle
 * COLUMN and left/right stay inside the outer ones.** Every other dimension
 * here follows from that one guarantee, and it is what lets the two pairs be
 * sized for completely different needs without competing:
 *
 * - Bottom and top run `POND_SEAT_DEPTH_RATIO` deep rather than a plain third.
 *   Their rows wrap downward, so depth is their scarce axis. It is capped to
 *   leave `POND_CENTRE_CLEARANCE_PX` between them for the seat compass.
 * - Left and right are sized by their own ratios, and **their height is what
 *   makes their piles read as vertical columns** — it buys the run length
 *   `gridSlots` fits before wrapping sideways. Being x-disjoint, they can be
 *   tall while bottom/top are deep; an earlier revision fitted them to the band
 *   *between* bottom and top instead, which coupled the two and squeezed the
 *   run down to two tiles, so the piles came out looking horizontal.
 *
 * They are narrower than the third they live in, and the slack becomes an
 * inward inset — clamped so they can never cross into the middle column.
 */
function computePondSeats(pond: Rect): Record<SeatPosition, Rect> {
  const cw = pond.width / POND_GRID;
  const depth = Math.min(
    pond.height * POND_SEAT_DEPTH_RATIO,
    Math.max(0, (pond.height - POND_CENTRE_CLEARANCE_PX) / 2),
  );
  const sideWidth = Math.min(pond.width * POND_SIDE_WIDTH_RATIO, cw);
  const sideHeight = pond.height * POND_SIDE_HEIGHT_RATIO;
  // The clamp is the non-overlap guarantee, not a nicety: past `cw - sideWidth`
  // these blocks would reach into the middle column and land on bottom/top.
  const inset = Math.min(pond.width * POND_SIDE_INSET_RATIO, cw - sideWidth);
  const sideY = pond.y + (pond.height - sideHeight) / 2;
  return {
    bottom: { x: pond.x + cw, y: pond.y + pond.height - depth, width: cw, height: depth },
    top: { x: pond.x + cw, y: pond.y, width: cw, height: depth },
    left: { x: pond.x + inset, y: sideY, width: sideWidth, height: sideHeight },
    right: {
      x: pond.x + pond.width - inset - sideWidth,
      y: sideY,
      width: sideWidth,
      height: sideHeight,
    },
  };
}

export class LayoutManager extends BaseLayoutManager<LayoutConfig> {
  protected compute(): LayoutConfig {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const mode = detectMode(viewportWidth);
    const orientation = detectOrientation(viewportWidth, viewportHeight);
    const isDesktop = mode === 'desktop';

    // Full-screen table, matching domino's canvas — fills the whole viewport
    // (minus a thin uniform margin) rather than being forced into a centred
    // square or shrunk to dodge the coach panel, so there's no dead backdrop
    // letterboxing either axis. The DOM coach panel docks as a FLOATING
    // overlay on top of the felt (see `coachSafeArea` below + `ui/CoachPanel`),
    // exactly like ActionBar/LessonCard/HandEndModal already float over the
    // canvas without reserving dedicated space.
    const margin = isDesktop ? TABLE_MARGIN_DESKTOP_PX : TABLE_MARGIN_MOBILE_PX;
    const coachWidth = isDesktop ? COACH_PANEL_WIDTH_PX : 0;
    const availW = Math.max(0, viewportWidth - margin * 2);
    const availH = Math.max(0, viewportHeight - margin * 2);

    const table: Rect = { x: margin, y: margin, width: availW, height: availH };

    const thickBasis = Math.min(table.width, table.height);
    const thick = Math.floor(Math.min(SEAT_ZONE_THICKNESS_PX, thickBasis * SEAT_ZONE_MAX_RATIO));
    const seatZones = computeSeatZones(table, thick);
    const pond: Rect = {
      x: table.x + thick,
      y: table.y + thick,
      width: table.width - thick * 2,
      height: table.height - thick * 2,
    };
    const pondSeats = computePondSeats(pond);

    // Where CoachPanel docks — a floating overlay, not a carve-out from
    // `table`/`availW` above, so it may sit on top of the table's right seat
    // band on desktop while the panel is shown (same tradeoff ActionBar and
    // LessonCard already make by floating over the table).
    const coachSafeArea: Rect = isDesktop
      ? { x: viewportWidth - coachWidth, y: 0, width: coachWidth, height: viewportHeight }
      : EMPTY_RECT;

    return {
      mode,
      orientation,
      viewport: { width: viewportWidth, height: viewportHeight },
      devicePixelRatio: window.devicePixelRatio || 1,
      table,
      seatZones,
      pond,
      pondSeats,
      coachSafeArea,
    };
  }

  protected equals(a: LayoutConfig, b: LayoutConfig): boolean {
    return (
      a.mode === b.mode &&
      a.orientation === b.orientation &&
      a.viewport.width === b.viewport.width &&
      a.viewport.height === b.viewport.height &&
      a.devicePixelRatio === b.devicePixelRatio &&
      a.table.x === b.table.x &&
      a.table.y === b.table.y &&
      a.table.width === b.table.width &&
      a.table.height === b.table.height &&
      a.coachSafeArea.width === b.coachSafeArea.width
    );
  }
}
